import { Component, OnInit, ChangeDetectorRef } from '@angular/core';
import { Router, RouterOutlet } from '@angular/router';
import { CommonModule } from '@angular/common';
import { MatSidenavModule } from '@angular/material/sidenav';
import { keycloakInstance } from './keycloak.config';
import { AuthService } from './auth/auth.service';
import { SidebarService } from './services/sidebar.service';
import { FooterComponent } from './components/footer/footer.component';
import { Language, LanguageService } from './services/language.service';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { MatMenuModule } from '@angular/material/menu';
import { MatButtonModule } from '@angular/material/button';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatIconModule } from '@angular/material/icon';

@Component({
  selector: 'app-root',
  standalone: true,
  imports: [CommonModule, MatIconModule, MatSidenavModule, FooterComponent, RouterOutlet, MatTooltipModule, MatMenuModule, MatButtonModule, TranslateModule],
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss']
})
export class AppComponent implements OnInit {
  user: any = null;
  isExpanded = true;
  currentLang!: Language;
  languages: Language[] = [
    { code: 'EL', name: 'Greek', flag: 'assets/flags/greece.png' },
    { code: 'EN', name: 'English', flag: 'assets/flags/united-kingdom.png' },
  ];

  constructor(
    private languageService: LanguageService,
    private authService: AuthService,
    public sidebarService: SidebarService,
    private cdr: ChangeDetectorRef,
    private translate: TranslateService,
    private router: Router
  ) {
    this.authService.user$.subscribe(user => {
      console.log('AppComponent: User data received', user);
      this.user = user;
    });
    
    // Set initial language from service
    this.languageService.currentLanguage$.subscribe(lang => {
      this.currentLang = lang;
      // Set the translation service language whenever currentLang changes
      this.translate.use(lang.code.toLowerCase());
      // Force change detection after language change
      this.cdr.markForCheck();
    });
  }

  selectLanguage(lang: Language): void {
    console.log('Selecting language:', lang);
    this.languageService.setLanguage(lang);
  }

  toggleLanguage(): void {
    console.log('Toggling language from:', this.currentLang.code);
    
    // Find the language that is not currently selected (only EN or EL)
    const filteredLanguages = this.languages.filter(lang => 
      lang.code === 'EN' || lang.code === 'EL'
    );
    
    const nextLang = filteredLanguages.find(lang => 
      lang.code !== this.currentLang.code
    );
    
    if (nextLang) {
      console.log('Switching to language:', nextLang.code);
      this.selectLanguage(nextLang);
    }
  }
  
  getNextLanguageFlag(): string {
    const filteredLanguages = this.languages.filter(lang => 
      lang.code === 'EN' || lang.code === 'EL'
    );
    
    const nextLang = filteredLanguages.find(lang => 
      lang.code !== this.currentLang.code
    );
    
    return nextLang ? nextLang.flag : 'assets/flags/united-kingdom.png';
  }
  
  getNextLanguageName(): string {
    const filteredLanguages = this.languages.filter(lang => 
      lang.code === 'EN' || lang.code === 'EL'
    );
    
    const nextLang = filteredLanguages.find(lang => 
      lang.code !== this.currentLang.code
    );
    
    return nextLang ? nextLang.name : 'English';
  }

  async ngOnInit(): Promise<void> {
    // Get initial language from service and set in translate service
    const initialLang = this.languageService.getCurrentLanguage();
    console.log('Initial language on init:', initialLang);
    
    if (initialLang) {
      this.translate.use(initialLang.code.toLowerCase());
    } else {
      // Fallback to a default language
      this.translate.use('el');
    }
    
    // Initialize Keycloak if needed
    // await this.initKeycloak();
  }

  toggleSidebar(isExpanded: any) {
    console.log(isExpanded);
    this.isExpanded = isExpanded;
  }

  login() {
    this.authService.login();
  }

  logout() {
    this.authService.logout();
  }

logoutKeycloak() {
  if (keycloakInstance) {
    keycloakInstance.logout({
      redirectUri: window.location.origin,
      logoutMethod: 'POST'
    });
  }
  }
  
  private async initKeycloak(): Promise<void> {
    const keycloak = keycloakInstance || (window as any).keycloakInstance;
    
    if (keycloak && !keycloak.didInitialize) {
      console.log('App component initializing Keycloak...');
      try {
        const authenticated = await keycloak.init({
          onLoad: 'check-sso',
          silentCheckSsoRedirectUri: window.location.origin + '/assets/silent-check-sso.html',
          checkLoginIframe: false
        });
        
        console.log('App Keycloak initialized, authenticated:', authenticated);
        
        // If authenticated and on landing page, redirect to personal-info
        if (authenticated && (this.router.url === '/' || this.router.url === '')) {
          console.log('User is authenticated at app level, redirecting to personal-info');
          setTimeout(() => {
            this.router.navigate(['/personal-info']);
          }, 100);
        }
      } catch (error) {
        console.error('Error initializing Keycloak at app level:', error);
      }
    } else if (keycloak && keycloak.authenticated) {
      console.log('App detected authenticated user on start');
      // Check if on landing page and redirect if needed
      if (this.router.url === '/' || this.router.url === '') {
        console.log('Redirecting from landing to personal-info');
        setTimeout(() => {
          this.router.navigate(['/input-meters']);
        }, 100);
      }
    }
  }
}